/**
 * DailyPricingStatePipeline · ARC-015 · Sprint 8.3.
 *
 * Renders the six-state Daily Pricing lifecycle as a compact horizontal
 * stepper plus a status-history timeline. Consumes
 * `GET /api/price-notifications/{id}/state` — presentation only.
 */
import { useEffect, useState } from "react";
import { CheckCircle, Circle, Prohibit, Clock } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const HAPPY_PATH = [
  "draft",
  "supplier_rack_recorded",
  "daily_buy_prices_entered",
  "selling_prices_generated",
  "approved",
  "distributed",
  "archived",
];
const LABELS = {
  draft:                    "Draft",
  supplier_rack_recorded:   "Supplier Rack Recorded",
  daily_buy_prices_entered: "Daily Buy Prices Entered",
  selling_prices_generated: "Selling Prices Generated",
  approved:                 "Approved",
  distributed:              "Distributed",
  archived:                 "Archived",
  cancelled:                "Cancelled",
};

function fmtDT(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month:"short", day:"2-digit", hour:"2-digit", minute:"2-digit" }); }
  catch { return iso; }
}

export default function DailyPricingStatePipeline({ notificationId }) {
  const [state, setState] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!notificationId) return;
    let alive = true;
    api.get(`/price-notifications/${notificationId}/state`)
      .then((r) => alive && setState(r.data))
      .catch((e) => alive && setErr(e?.response?.data?.detail || "state unavailable"));
    return () => { alive = false; };
  }, [notificationId]);

  if (err) return null;
  if (!state) return null;

  const current = state.business_state;
  const cancelled = current === "cancelled";
  const currentIdx = HAPPY_PATH.indexOf(current);

  return (
    <section
      data-testid="daily-pricing-state-pipeline"
      className="rounded-lg border border-slate-200 bg-white p-4"
    >
      <div className="flex items-baseline gap-3 mb-3">
        <h3 className="text-sm font-semibold text-slate-800">Daily Pricing Lifecycle</h3>
        <span className="text-[11px] text-slate-500 uppercase tracking-wider">ARC-015</span>
        <span
          data-testid="pipeline-current-state"
          className={`ml-auto inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
            cancelled
              ? "border-rose-300 bg-rose-50 text-rose-800"
              : current === "distributed" || current === "archived"
                ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                : "border-sky-300 bg-sky-50 text-sky-800"
          }`}
        >
          {LABELS[current] || current}
        </span>
      </div>

      {cancelled ? (
        <div
          data-testid="pipeline-cancelled-banner"
          className="mb-3 flex items-center gap-2 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900"
        >
          <Prohibit size={16} weight="fill" /> This package was cancelled. Off happy-path.
        </div>
      ) : (
        <ol className="flex items-center flex-wrap gap-y-2" data-testid="pipeline-steps">
          {HAPPY_PATH.map((s, i) => {
            const done = i < currentIdx;
            const active = i === currentIdx;
            return (
              <li key={s} className="flex items-center gap-1.5">
                <span
                  data-testid={`pipeline-step-${s}`}
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                    done
                      ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                      : active
                        ? "border-sky-500 bg-sky-100 text-sky-900 font-semibold"
                        : "border-slate-200 bg-white text-slate-500"
                  }`}
                >
                  {done ? <CheckCircle size={11} weight="fill" /> : active ? <Clock size={11} weight="fill" /> : <Circle size={10} weight="regular" />}
                  {LABELS[s]}
                </span>
                {i < HAPPY_PATH.length - 1 && (
                  <span className="text-slate-300" aria-hidden="true">→</span>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {state.status_history && state.status_history.length > 0 && (
        <details className="mt-4" data-testid="pipeline-history">
          <summary className="cursor-pointer text-xs font-semibold text-slate-700 hover:text-slate-900">
            Status history ({state.status_history.length} transition{state.status_history.length === 1 ? "" : "s"})
          </summary>
          <ol className="mt-2 space-y-1 text-[12px]">
            {state.status_history.map((h, i) => (
              <li key={i} data-testid={`pipeline-history-${i}`} className="flex items-baseline gap-2 border-l-2 border-slate-200 pl-2">
                <span className="text-slate-500 tabular-nums whitespace-nowrap">{fmtDT(h.at)}</span>
                <span className="text-slate-800">
                  <b>{LABELS[h.from] || h.from}</b> → <b>{LABELS[h.to] || h.to}</b>
                </span>
                <span className="text-slate-500">· {h.by || "system"}</span>
                {h.reason && <span className="text-slate-500 italic">— "{h.reason}"</span>}
              </li>
            ))}
          </ol>
        </details>
      )}
    </section>
  );
}
